/* Team access model: the real per-account grants — web console Permission flags
   and the client CRM's permissions — grouped and preset the way the Team page
   shows them (ADR 0017/0018/0019). Pure functions only. */

import type { AccountAssignment, AccountPermissions, CrmAccess, CrmFanAccess, CrmLevel, CrmPermission } from '../../../api/client';

export type Flag = keyof AccountPermissions;
/** Media download (CONTEXT.md): stored with the CRM grants, but not a CRM
    permission — it gives no access alone, so it is only held alongside one. */
export const MEDIA_DOWNLOAD = 'crm.mediaDownload' as const;
/** A CRM grant as a checkbox key (kept apart from the flag namespace). */
export type CrmKey = `crm.${CrmPermission}` | typeof MEDIA_DOWNLOAD;
export type Key = Flag | CrmKey;

export const CRM_PERMISSIONS: CrmPermission[] = ['chats', 'vault', 'collections', 'queue', 'mass', 'profile', 'statements', 'earnings'];
export const crmKey = (p: CrmPermission): CrmKey => `crm.${p}`;
/** Holds at least one CRM permission (what Media download needs). */
export const holdsCrm = (has: ReadonlySet<Key>): boolean => CRM_PERMISSIONS.some((p) => has.has(crmKey(p)));
/** The CRM permission a checkbox item id (`crm-<permission>`) stands for, if any. */
export function crmOfItem(id: string): CrmPermission | null {
  const p = id.startsWith('crm-') ? id.slice(4) : '';
  return (CRM_PERMISSIONS as string[]).includes(p) ? (p as CrmPermission) : null;
}
const isCrmKey = (k: Key): k is CrmKey => k.startsWith('crm.');

/** One checkbox. `keys` > 1 folds flags the UI never splits (campaigns + schedules). */
export interface PermItem { id: string; labelKey: string; keys: Key[] }
/** A group; `master` is the section's own gate, required by every other item in it. */
export interface PermGroup { id: string; labelKey: string; master: Key | null; items: PermItem[] }

export const GROUPS: PermGroup[] = [
  {
    id: 'crm', labelKey: 'team.crm', master: null,
    items: CRM_PERMISSIONS.map((p) => ({ id: `crm-${p}`, labelKey: `fleet.crm.${p}`, keys: [crmKey(p)] })),
  },
  {
    // Saving the account's media out of the CRM; needs a CRM permission.
    id: 'mediaDownload', labelKey: 'team.mediaDownload', master: null,
    items: [{ id: 'mediaDownload', labelKey: 'team.mediaDownload', keys: [MEDIA_DOWNLOAD] }],
  },
  {
    id: 'chatAssistant', labelKey: 'team.chatAssistant', master: 'access_chat_assistant',
    items: [
      { id: 'ca', labelKey: 'fleet.perm.basic', keys: ['access_chat_assistant'] },
      { id: 'caSetup', labelKey: 'team.assistantSetup', keys: ['access_assistant_setup'] },
      { id: 'caContent', labelKey: 'team.assistantContent', keys: ['access_assistant_content'] },
      { id: 'caModel', labelKey: 'team.modelInfo', keys: ['access_model_info'] },
      { id: 'caCensure', labelKey: 'team.censure', keys: ['access_censure'] },
      { id: 'caAudio', labelKey: 'team.audioSettings', keys: ['access_audio_settings'] },
    ],
  },
  {
    // Revenue and stats of the account on the Statistics page; needs no AI access.
    id: 'stats', labelKey: 'team.stats', master: null,
    items: [{ id: 'stats', labelKey: 'team.stats', keys: ['access_stats'] }],
  },
  {
    id: 'mass', labelKey: 'team.massMessages', master: 'access_mass_messages',
    items: [
      { id: 'mm', labelKey: 'fleet.perm.basic', keys: ['access_mass_messages'] },
      { id: 'mmLists', labelKey: 'fleet.perm.lists', keys: ['access_mass_messages_lists'] },
      { id: 'mmSend', labelKey: 'fleet.perm.campaigns', keys: ['access_mass_messages_campaigns', 'access_mass_messages_schedules'] },
    ],
  },
  {
    id: 'priority', labelKey: 'team.priorityMassMessages', master: 'access_priority_mass_messages',
    items: [
      { id: 'pm', labelKey: 'fleet.perm.basic', keys: ['access_priority_mass_messages'] },
      { id: 'pmLists', labelKey: 'fleet.perm.lists', keys: ['access_priority_mass_messages_lists'] },
      { id: 'pmSend', labelKey: 'fleet.perm.campaigns', keys: ['access_priority_mass_messages_campaigns', 'access_priority_mass_messages_schedules'] },
    ],
  },
  {
    id: 'automated', labelKey: 'team.automatedMessages', master: 'access_automated_messages',
    items: [
      { id: 'am', labelKey: 'fleet.perm.basic', keys: ['access_automated_messages'] },
      { id: 'amSetup', labelKey: 'team.automatedMessagesSetup', keys: ['access_automated_messages_setup'] },
    ],
  },
  {
    // Tools (ADR 0031): each one only on the Accounts where it is granted.
    id: 'tools', labelKey: 'team.tools', master: null,
    items: [
      { id: 'toolBulkMedia', labelKey: 'team.bulkMedia', keys: ['access_bulk_media'] },
      { id: 'toolAudio', labelKey: 'team.aiAudio', keys: ['access_audio'] },
      { id: 'toolDynamicLists', labelKey: 'team.dynamicLists', keys: ['access_dynamic_lists'] },
      { id: 'toolTrackingLinks', labelKey: 'team.trackingLinks', keys: ['access_tracking_links'] },
    ],
  },
  {
    id: 'account', labelKey: 'team.account', master: null,
    items: [
      { id: 'acNotif', labelKey: 'team.notificationsSettings', keys: ['access_notifications_settings'] },
      { id: 'acToken', labelKey: 'team.accountSettingsToken', keys: ['access_token'] },
      { id: 'acPay', labelKey: 'team.pay', keys: ['access_pay'] },
      { id: 'acReconnect', labelKey: 'fleet.perm.reconnect', keys: ['access_reconnect'] },
      { id: 'acPermissions', labelKey: 'team.permissionManagement', keys: ['access_permission_management'] },
    ],
  },
];

/** Every key a person can be granted (the structural gates are set implicitly). */
export const GRANTABLE: Key[] = GROUPS.flatMap((g) => g.items.flatMap((i) => i.keys));
const FLAGS = GRANTABLE.filter((k): k is Flag => !isCrmKey(k));

export type PresetId = 'full' | 'chatter' | 'none';
export const PRESETS: { id: PresetId; labelKey: string; tipKey: string; keys: Key[] }[] = [
  { id: 'full', labelKey: 'fleet.preset.full', tipKey: 'fleet.preset.fullTip', keys: GRANTABLE },
  {
    // CRM work only: no AI tools, no revenue (Stats access / Earnings), no Statements.
    id: 'chatter', labelKey: 'fleet.preset.chatter', tipKey: 'fleet.preset.chatterTip',
    keys: (['chats', 'vault', 'collections', 'queue', 'mass', 'profile'] as CrmPermission[]).map(crmKey),
  },
  { id: 'none', labelKey: 'fleet.preset.none', tipKey: 'fleet.preset.noneTip', keys: [] },
];

/** One person's grants on one account: checked keys, the CRM permissions
    narrowed to View only, and Per-subscriber access entries. */
export interface Access { has: Set<Key>; view: Set<CrmPermission>; fans: CrmFanAccess[] }

export const NO_ACCESS: Access = { has: new Set(), view: new Set(), fans: [] };

export function fromAssignment(a: AccountAssignment | undefined): Access {
  if (!a) return NO_ACCESS;
  const has = new Set<Key>(FLAGS.filter((k) => a.permissions?.[k]));
  const view = new Set<CrmPermission>();
  for (const [p, level] of Object.entries(a.crm?.permissions ?? {}) as [CrmPermission, CrmLevel][]) {
    has.add(crmKey(p));
    if (level === 'view') view.add(p);
  }
  if (a.crm?.media_download && holdsCrm(has)) has.add(MEDIA_DOWNLOAD);
  return { has, view, fans: a.crm?.fans ?? [] };
}

export const isEmpty = (a: Access) => a.has.size === 0;

/** The preset these grants match exactly, or null (= Custom). Anything set
    under Advanced is always Custom. */
export function presetOf(a: Access): PresetId | null {
  if (a.view.size || a.fans.length) return null;
  const hit = PRESETS.find((p) => p.keys.length === a.has.size && p.keys.every((k) => a.has.has(k)));
  return hit ? hit.id : null;
}

/** Apply the section rules: a sub-item needs its section's gate; levels and
    subscriber entries only exist for what is granted (entries need Chats). */
function normalise(a: Access): Access {
  const has = new Set(a.has);
  for (const g of GROUPS) {
    if (!g.master) continue;
    const subs = g.items.flatMap((i) => i.keys).filter((k) => k !== g.master);
    if (subs.some((k) => has.has(k))) has.add(g.master);
  }
  if (!holdsCrm(has)) has.delete(MEDIA_DOWNLOAD);
  const view = new Set([...a.view].filter((p) => has.has(crmKey(p))));
  const fans = has.has(crmKey('chats')) ? a.fans : [];
  return { has, view, fans };
}

export function toggleItem(a: Access, item: PermItem, group: PermGroup): Access {
  const on = item.keys.every((k) => a.has.has(k));
  const has = new Set(a.has);
  if (on) {
    item.keys.forEach((k) => has.delete(k));
    // Turning the gate off takes the whole section with it.
    if (group.master && item.keys.includes(group.master)) group.items.forEach((i) => i.keys.forEach((k) => has.delete(k)));
  } else {
    item.keys.forEach((k) => has.add(k));
  }
  return normalise({ ...a, has });
}

export function toggleGroup(a: Access, group: PermGroup): Access {
  const keys = group.items.flatMap((i) => i.keys);
  const all = keys.every((k) => a.has.has(k));
  const has = new Set(a.has);
  keys.forEach((k) => (all ? has.delete(k) : has.add(k)));
  return normalise({ ...a, has });
}

export const applyPreset = (id: PresetId): Access =>
  ({ has: new Set(PRESETS.find((p) => p.id === id)?.keys ?? []), view: new Set(), fans: [] });

export const levelOf = (a: Access, p: CrmPermission): CrmLevel => (a.view.has(p) ? 'view' : 'full');

export function setLevel(a: Access, p: CrmPermission, level: CrmLevel): Access {
  const view = new Set(a.view);
  if (level === 'view') view.add(p); else view.delete(p);
  // A subscriber can never get more than Chats itself allows.
  const fans = p === 'chats' && level === 'view'
    ? a.fans.map((f) => (f.level === 'full' ? { ...f, level: 'view' as const } : f))
    : a.fans;
  return normalise({ ...a, view, fans });
}

export const setFans = (a: Access, fans: CrmFanAccess[]): Access => normalise({ ...a, fans });

/** What the API stores for these grants: the full flag row plus CRM permissions. */
export function toPayload(a: Access): { permissions: AccountPermissions; crm: CrmAccess } {
  const permissions = {} as AccountPermissions;
  FLAGS.forEach((k) => { permissions[k] = a.has.has(k); });
  permissions.owner_access_to_account = false;
  permissions.access_to_account = true;
  const crm: CrmAccess = { permissions: {}, fans: a.fans, media_download: a.has.has(MEDIA_DOWNLOAD) };
  CRM_PERMISSIONS.forEach((p) => { if (a.has.has(crmKey(p))) crm.permissions[p] = levelOf(a, p); });
  return { permissions, crm };
}

/** The CRM permissions currently granted, in display order. */
export const grantedCrm = (a: Access): CrmPermission[] => CRM_PERMISSIONS.filter((p) => a.has.has(crmKey(p)));

export const groupState = (a: Access, g: PermGroup): 'all' | 'some' | 'none' => {
  const keys = g.items.flatMap((i) => i.keys);
  const n = keys.filter((k) => a.has.has(k)).length;
  return n === 0 ? 'none' : n === keys.length ? 'all' : 'some';
};

/** The same grants: checked keys, View-only levels and subscriber entries. */
export function sameAccess(a: Access, b: Access): boolean {
  const same = <T,>(x: Set<T>, y: Set<T>) => x.size === y.size && [...x].every((k) => y.has(k));
  const fans = (x: Access) => JSON.stringify([...x.fans].sort((m, n) => m.fan_id.localeCompare(n.fan_id)));
  return same(a.has, b.has) && same(a.view, b.view) && fans(a) === fans(b);
}

/** One pair's grants under a bulk change: the template's grants, keeping the
    pair's own Per-subscriber entries — they name one account's subscribers, so
    they never copy across — narrowed to what the template's Chats allows. */
export function forPair(template: Access, existing: Access): Access {
  const chatsView = template.view.has('chats');
  const fans = existing.fans.map((x) => (chatsView && x.level === 'full' ? { ...x, level: 'view' as const } : x));
  return setFans({ has: new Set(template.has), view: new Set(template.view), fans: [] }, fans);
}
